/*
 * File: store.ts
 * Project: beaker
 * -----
 * Last Modified: 15 Mar 2022 10:33:40 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { AnyAction, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { abbyCadabbyApi, abbyCadabbyParams } from './services/abbyCadabby';
import { animalsApi, animalsConditionsParams, animalsDispositionsParams, animalsOwnersParams } from './services/animals';
import { beautifulDayMonsterApi, beautifulDayMonsterCustomersParams } from './services/beautifulDayMonster';
import { chuckieSueAddressesApi, chuckieSueAddressesParams, chuckieSueWorldsApi } from './services/chuckieSue';
import { userParams } from './services/cookieMonster';
import { cookieMonsterApi } from './services/cookieMonster/cookieMonsterApi';
import { donaldGrumpApi, donaldGrumpProductMastersParams, donaldGrumpReleasedProductsParams } from './services/donaldGrump';
import { elmoApi } from './services/elmo';
import { freddyCadabbyApi, freddyCadabbyParams } from './services/freddyCadabby';
import { juliaApi, juliaProductCatalogParams } from './services/julia';
import { leftyTheSalesman } from './services/leftyTheSalesman';
import { legalEntitiesApi } from './services/maggieCadabby/useMaggieCadabbyService';
import { mrsGrouchApi } from './services/mrsGrouch/mrsGrouchApi';
import { projectParams } from './services/mrsGrouch/projectsParams';
import { rudyCadabbyApi } from './services/rudyCadabby/rudyCadabbyApi';
import { appSlice, customerSelectionFiltersSlice } from './slices';
import { businessListSlice } from './slices/businessListSlice';
import { selectedProjectsSlice } from './slices/selectedProjectsSlice';

export const store = configureStore({
  reducer: {
    [appSlice.name]: appSlice.reducer,
    [cookieMonsterApi.reducerPath]: cookieMonsterApi.reducer,
    [userParams.name]: userParams.reducer,
    [customerSelectionFiltersSlice.name]: customerSelectionFiltersSlice.reducer,
    [businessListSlice.name]: businessListSlice.reducer,
    [selectedProjectsSlice.name]: selectedProjectsSlice.reducer,
    // Chuckie Sue
    [chuckieSueAddressesApi.reducerPath]: chuckieSueAddressesApi.reducer,
    [chuckieSueAddressesParams.name]: chuckieSueAddressesParams.reducer,
    [chuckieSueWorldsApi.reducerPath]: chuckieSueWorldsApi.reducer,
    // Julia
    [juliaApi.reducerPath]: juliaApi.reducer,
    [juliaProductCatalogParams.name]: juliaProductCatalogParams.reducer,
    // Abby Cadabby
    [abbyCadabbyApi.reducerPath]: abbyCadabbyApi.reducer,
    [abbyCadabbyParams.name]: abbyCadabbyParams.reducer,
    // Freddy Cadabby
    [freddyCadabbyApi.reducerPath]: freddyCadabbyApi.reducer,
    [freddyCadabbyParams.name]: freddyCadabbyParams.reducer,
    // Mrs Grouch
    [mrsGrouchApi.reducerPath]: mrsGrouchApi.reducer,
    [projectParams.name]: projectParams.reducer,

    // Beautiful Day Monster
    [beautifulDayMonsterApi.reducerPath]: beautifulDayMonsterApi.reducer,
    [beautifulDayMonsterCustomersParams.name]: beautifulDayMonsterCustomersParams.reducer,
    // Donald Grump
    [donaldGrumpApi.reducerPath]: donaldGrumpApi.reducer,
    [donaldGrumpProductMastersParams.name]: donaldGrumpProductMastersParams.reducer,
    [donaldGrumpReleasedProductsParams.name]: donaldGrumpReleasedProductsParams.reducer,
    // Animals
    [animalsApi.reducerPath]: animalsApi.reducer,
    [animalsOwnersParams.name]: animalsOwnersParams.reducer,
    [animalsConditionsParams.name]: animalsConditionsParams.reducer,
    [animalsDispositionsParams.name]: animalsDispositionsParams.reducer,
    // Rudy Cadabby
    [rudyCadabbyApi.reducerPath]: rudyCadabbyApi.reducer,
    // Lefty The Salesman
    [leftyTheSalesman.reducerPath]: leftyTheSalesman.reducer,
    // Maggie Cadabby
    [legalEntitiesApi.reducerPath]: legalEntitiesApi.reducer,
    // Elmo
    [elmoApi.reducerPath]: elmoApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      freddyCadabbyApi.middleware,
      chuckieSueAddressesApi.middleware,
      chuckieSueWorldsApi.middleware,
      juliaApi.middleware,
      abbyCadabbyApi.middleware,
      mrsGrouchApi.middleware,
      beautifulDayMonsterApi.middleware,
      donaldGrumpApi.middleware,
      animalsApi.middleware,
      rudyCadabbyApi.middleware,
      cookieMonsterApi.middleware,
      leftyTheSalesman.middleware,
      legalEntitiesApi.middleware,
      elmoApi.middleware
    ])
});

export type ReduxState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type ThunkAppDispatch = ThunkDispatch<ReduxState, void, AnyAction>;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

setupListeners(store.dispatch);
export default store;
